/**
 * GENERATED by /docs-writer from docs/mcp.md — DO NOT EDIT BY HAND.
 * Edit the markdown and run `/docs-writer update docs/mcp.md`.
 *
 * source-sha: 92b07bd59bca9aa0839cd92b84607b8b1f158593
 * sources (from the markdown's "Sources" section):
 *   - Tool catalog, categories, the writes flag, the withheld field: packages/shared-types/src/mcp.ts
 *   - Role tiers, secret levels, dashboard-only rules: packages/shared-types/src/rbac.ts
 *   - Auth, role lookup on every call, list filtering and refusal, health answer: apps/mcp/src/main.ts
 *   - One call step by step (token check, role lookup, tool gate, level filter, hidden-match count): apps/mcp/src/main.ts, packages/inventory-core/src/pat.ts, packages/inventory-core/src/roles.ts, packages/inventory-core/src/search.ts, apps/mcp/src/handlers.ts (search)
 *   - Self-only cancel, known-person guard, incident preview, level filters: apps/mcp/src/handlers.ts, pinned by apps/mcp/test/mcp-e2e.spec.ts and apps/mcp/test/incident-preview.spec.ts
 *   - Token panel, one-time display, 90-day life, revoke: apps/web/src/components/McpTokens.tsx, apps/web/src/pages/AccountPage.tsx, apps/api/src/app/pat/pat.service.ts, packages/inventory-core/src/pat.ts
 *   - Dashboard side of each difference: apps/api/src/app/access-requests/access-requests.resolver.ts, apps/api/src/app/jobs/jobs.resolver.ts, apps/api/src/app/incidents/incidents.resolver.ts, apps/api/src/app/inventory/repo-ownership.resolver.ts, apps/web/src/pages/AgentsPage.tsx
 *   - Claude Code set-up, private local scope, .mcp.json is committed: claude mcp add --help and the Claude Code MCP documentation, read 2026-10-10
 *   - Leads checked against the code: CLAUDE.md (MCP auth, RBAC, access requests, update_repo, run_job)
 */
import { Link } from 'react-router';
import { DocSequence } from '../components/DocSequence';
import { DocsToc } from '../components/DocsToc';
import { McpConnectSteps } from '../components/docs/live/McpConnectSteps';
import { McpToolReference } from '../components/docs/live/McpToolReference';

const TOC = [
  { id: 'connect', label: 'Connect your AI client' },
  { id: 'what-your-role-can-call', label: 'What your role can call' },
  { id: 'which-secrets-your-role-sees', label: 'Which secrets your role sees' },
  { id: 'what-only-the-dashboard-can-do', label: 'What only the dashboard can do' },
  { id: 'tool-reference', label: 'Tool reference' },
];

const YES = (
  <span className="doc-yes">
    <span className="doc-vh">allowed</span>
  </span>
);
const NO = (
  <span className="doc-no">
    <span className="doc-vh">blocked</span>
  </span>
);

export function McpDocsPage() {
  return (
    <>
      <div className="page doc" id="top">
        <p className="doc-eyebrow">MCP · Reference</p>
        <h1 className="doc-title">MCP tools</h1>
        <p className="doc-lede">
          The MCP server gives your AI client the inventory this dashboard shows: repos, clusters, databases,
          secret names, scheduler jobs, deploy catalogs, tasks, access requests and alerts. You connect with a personal
          access token, and your role in this dashboard decides which tools the client is given. Most tools only
          read, and no tool returns a secret value.
        </p>
        <p className="doc-meta">
          Updated <time dateTime="2026-10-12">12 Oct 2026</time>
        </p>

        <h2 className="doc-h2" id="connect">Connect your AI client</h2>
        <p className="doc-p">
          The server is hosted at one address, so there is nothing to install. Each person connects with their
          own token. These steps are for Claude Code.
        </p>
        <McpConnectSteps />
        <p className="doc-p">
          The dashboard shows a token once, when you create it, and the token expires 90 days later. To cut a
          client off sooner, revoke its token on <Link to="/settings/profile">your Account page</Link>. The
          server refuses a revoked token on the next call.
        </p>
        <aside className="doc-note doc-note--warn">
          <span className="doc-note-label">Warning</span>
          <p>
            Do not paste a token into a project&rsquo;s <code>.mcp.json</code>. Claude Code expects that file to
            be committed, so the token would reach everyone who has the repo. The command in step 2 stores the
            server for you alone. If your team shares the server in <code>.mcp.json</code>, write the header as{' '}
            <code>Bearer ${'{'}YOUR_VARIABLE{'}'}</code> and keep the token in your own environment.
          </p>
        </aside>

        <h2 className="doc-h2" id="what-your-role-can-call">What your role can call</h2>
        <p className="doc-p">
          On every call the server looks up the role your email has in this dashboard. The diagram follows one
          call to <code>search</code>.
        </p>
        <DocSequence
          caption="One call to search, from your question to the filtered answer"
          participants={[
            { id: 'You', label: 'You', kind: 'person' },
            { id: 'Client', label: 'AI client' },
            { id: 'Mcp', label: 'MCP server' },
            { id: 'Db', label: 'Dashboard database', note: 'tokens, roles, inventory' },
          ]}
          steps={[
            { from: 'You', to: 'Client', text: 'Ask about the inventory' },
            { from: 'Client', to: 'Mcp', text: <>Call <code>search</code> with your token</> },
            { from: 'Mcp', to: 'Db', text: 'Look up the token' },
            { kind: 'reply', from: 'Db', to: 'Mcp', text: 'The email that owns it' },
            {
              kind: 'note',
              over: ['Client', 'Mcp'],
              text: (
                <>
                  The server answers 401 <code>unauthorized</code> to a revoked, expired or unknown token. The
                  call stops here.
                </>
              ),
            },
            { from: 'Mcp', to: 'Db', text: <>Look up that email&rsquo;s role</> },
            { kind: 'reply', from: 'Db', to: 'Mcp', text: 'Your role' },
            { from: 'Mcp', to: 'Mcp', text: 'Check the tool against your role' },
            {
              kind: 'note',
              over: ['Client', 'Mcp'],
              text: (
                <>
                  The server refuses a tool your role may not call. That tool is also missing from the list your
                  client receives.
                </>
              ),
            },
            { from: 'Mcp', to: 'Db', text: 'Search what your role may see' },
            { kind: 'reply', from: 'Db', to: 'Mcp', text: 'Matches, hidden secrets left out' },
            { from: 'Mcp', to: 'Db', text: 'Count the hidden secret matches' },
            { kind: 'reply', from: 'Db', to: 'Mcp', text: 'How many are hidden' },
            { kind: 'reply', from: 'Mcp', to: 'Client', text: <>Matches and the <code>withheld</code> count</> },
          ]}
        />
        <p className="doc-p">
          A role change applies on the next call, with the same token. An email with no role yet counts as a{' '}
          <strong>new member</strong>.
        </p>
        <p className="doc-p">
          The reference below tags each tool with its tier. A ✓ means the client lists the tool and may call it.
          A – means the tool is hidden and refused.
        </p>
        <div className="doc-table doc-matrix">
          <table>
            <thead>
              <tr>
                <th scope="col">Tools tagged</th>
                <th scope="col">root</th>
                <th scope="col">maintainer</th>
                <th scope="col">engineer</th>
                <th scope="col">new member</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row"><strong>new member</strong></th>
                <td>{YES}</td>
                <td>{YES}</td>
                <td>{YES}</td>
                <td>{YES}</td>
              </tr>
              <tr>
                <th scope="row"><strong>no role tag</strong></th>
                <td>{YES}</td>
                <td>{YES}</td>
                <td>{YES}</td>
                <td>{NO}</td>
              </tr>
              <tr>
                <th scope="row"><strong>root or maintainer</strong></th>
                <td>{YES}</td>
                <td>{YES}</td>
                <td>{NO}</td>
                <td>{NO}</td>
              </tr>
              <tr>
                <th scope="row"><strong>root only</strong></th>
                <td>{YES}</td>
                <td>{NO}</td>
                <td>{NO}</td>
                <td>{NO}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          A new member can search, read the deploy catalogs, work on their own access requests and file and
          work on their own plain tasks. That is enough to check a token, to ask for access and to ask
          engineers for help. A root user assigns roles on Administration → Users
          &amp; roles.
        </p>
        <p className="doc-p">
          The <strong>writes</strong> tag marks a tool that changes data. A tool without it only reads.
        </p>

        <h2 className="doc-h2" id="which-secrets-your-role-sees">Which secrets your role sees</h2>
        <p className="doc-p">
          The secret tools return key names and their descriptions. They never return a value, because the
          inventory stores none.
        </p>
        <p className="doc-p">
          Every secret in the inventory has a level: <strong>L0</strong> (critical), <strong>L1</strong>{' '}
          (restricted) or <strong>L3</strong> (normal). L2 is not used. Your role decides which levels the secret
          tools and search return. A ✓ means the role sees secrets of that level. A – means the tools leave them
          out.
        </p>
        <div className="doc-table doc-matrix">
          <table>
            <thead>
              <tr>
                <th scope="col">Role</th>
                <th scope="col">L0 critical</th>
                <th scope="col">L1 restricted</th>
                <th scope="col">L3 normal</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row"><strong>root</strong></th>
                <td>{YES}</td>
                <td>{YES}</td>
                <td>{YES}</td>
              </tr>
              <tr>
                <th scope="row"><strong>maintainer</strong></th>
                <td>{NO}</td>
                <td>{YES}</td>
                <td>{YES}</td>
              </tr>
              <tr>
                <th scope="row"><strong>engineer</strong></th>
                <td>{NO}</td>
                <td>{NO}</td>
                <td>{YES}</td>
              </tr>
              <tr>
                <th scope="row"><strong>new member</strong></th>
                <td>{NO}</td>
                <td>{NO}</td>
                <td>{NO}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <aside className="doc-note">
          <span className="doc-note-label">Note</span>
          <p>
            Search says when it hides a match. A <code>withheld</code> count above zero means secrets match your
            query at a level your role does not see. Ask for access on <Link to="/requests">Requests</Link>{' '}
            instead of concluding that nothing exists.
          </p>
        </aside>

        <h2 className="doc-h2" id="what-only-the-dashboard-can-do">What only the dashboard can do</h2>
        <p className="doc-p">
          The MCP surface is narrower than the dashboard on purpose. An agent can read, prepare and recommend,
          and a person on the dashboard makes the call. The two differ in these places.
        </p>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Action</th>
                <th>Over MCP</th>
                <th>On the dashboard</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Decide an access request</strong></td>
                <td>No tool. An agent can file a request, comment on it, edit its reason and withdraw it.</td>
                <td>A reviewer approves, denies or escalates.</td>
              </tr>
              <tr>
                <td><strong>Work on plain tasks</strong></td>
                <td>
                  <code>create_task</code>, <code>list_tasks</code>, <code>get_task</code>, <code>comment_task</code>,{' '}
                  <code>update_task</code>, <code>close_task</code> and <code>reopen_task</code>, with the dashboard&rsquo;s
                  rules. Access to a company system goes to <code>create_access_request</code> instead.
                </td>
                <td>The same rules on Tasks.</td>
              </tr>
              <tr>
                <td><strong>Cancel a request</strong></td>
                <td>Your own request only.</td>
                <td>A root user can also cancel someone else&rsquo;s.</td>
              </tr>
              <tr>
                <td><strong>Assign a repo&rsquo;s Maintainer</strong></td>
                <td>
                  Root or maintainer only, with <code>update_repo</code>.
                </td>
                <td>The repo&rsquo;s Owner can also assign it.</td>
              </tr>
              <tr>
                <td><strong>Assign someone the app does not know</strong></td>
                <td>
                  Refused. <code>update_repo</code> takes only an email that <code>list_assignable_users</code>{' '}
                  returns.
                </td>
                <td>A root user can assign a person who has not signed in yet.</td>
              </tr>
              <tr>
                <td><strong>Run a scheduler job</strong></td>
                <td>
                  Root only, with <code>run_job</code>.
                </td>
                <td>Root or engineer, with Run now.</td>
              </tr>
              <tr>
                <td><strong>Send an incident notification</strong></td>
                <td>
                  <code>preview_incident_notification</code> shows who would be told and the email. It sends
                  nothing.
                </td>
                <td>Operations → Notify owners sends it.</td>
              </tr>
              <tr>
                <td><strong>Run a cluster agent command</strong></td>
                <td>No tool.</td>
                <td>A person plans, drafts and approves each command.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2 className="doc-h2" id="tool-reference">Tool reference</h2>
        <p className="doc-p">
          The reference lists every tool the server registers, by category. It is drawn from the catalog the
          server itself reads, so it matches what a root user&rsquo;s client receives. Your own client lists the
          part your role allows.
        </p>
        <McpToolReference />

        <aside className="doc-next">
          <div>
            <h2 className="doc-next-title">New here?</h2>
            <p>
              Get started walks you through your token, your AI client and your role, and marks each step as you
              finish it.
            </p>
          </div>
          <Link className="btn" to="/docs/get-started">
            Open Get started
          </Link>
        </aside>

        <p className="doc-back">
          <a href="#top">Back to top ↑</a>
        </p>
      </div>
      <DocsToc items={TOC} />
    </>
  );
}
